// Tali's Nailz — shared chat component.
//
// One module, three mounts: the nail-profile pending screen, the BYO proof
// page, and the staff inbox. Follows the inspo.js precedent (IIFE, one global).
//
//   const chat = TNChat.mount(el, {
//     auth: 'customer' | 'link' | 'staff',
//     threadKey: { kind: 'profile' } | { threadId: 'thr_...' },
//     role: 'customer' | 'staff',
//     placeholder: 'Message Tali…',
//     onUnread: fn, onAuthExpired: fn
//   });
//
// Two behaviours worth knowing about before changing anything here:
//
//  * Polling PAUSES entirely while the tab is hidden. This is not a nicety —
//    it is what keeps the D1 free tier viable.
//  * Every send carries a clientId. The server has a UNIQUE(thread_id,
//    client_id) index, so a retry is a no-op rather than a duplicate. The poll
//    loop reconciles by clientId instead of appending, which is what stops the
//    classic "my own message appears twice" bug.

(function () {
  'use strict';

  var POLL_ACTIVE_MS = 5000;
  var POLL_IDLE_MS = 15000;
  var ACTIVE_WINDOW_MS = 5 * 60 * 1000;
  var MAX_BACKOFF_MS = 60000;
  var MAX_FILES = 4;
  var MAX_FILE_BYTES = 8 * 1024 * 1024;

  function tn() { return window.TALISNAILZ || {}; }
  function workerUrl() { return tn().workerUrl || ''; }

  function post(path, body) {
    return fetch(workerUrl() + path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body || {})
    }).then(function (r) {
      return r.json().catch(function () { return { ok: false, error: 'Bad response' }; })
        .then(function (data) { return { status: r.status, data: data }; });
    });
  }

  function el(tag, cls, text) {
    var node = document.createElement(tag);
    if (cls) node.className = cls;
    if (text != null) node.textContent = text;
    return node;
  }

  /**
   * A display-sized copy of a Cloudinary image.
   *
   * A bubble shows a photo a few hundred pixels wide, but the stored file is
   * whatever the phone or screenshot produced -- 4.3MB PNGs turned up in real
   * use. Rendering those untransformed meant a single chat photo downloaded
   * more than the entire rest of the site, and six of them made the page look
   * broken while it churned.
   *
   * f_auto serves webp or avif where supported, q_auto picks a quality, and
   * c_limit never upscales something already small. Non-Cloudinary URLs do not
   * match and pass through untouched.
   */
  function thumbUrl(url) {
    if (typeof url !== 'string') return url;
    return url.replace('/image/upload/', '/image/upload/f_auto,q_auto,w_600,c_limit/');
  }

  function uuid() {
    if (window.crypto && crypto.randomUUID) return crypto.randomUUID();
    return 'c-' + Date.now() + '-' + Math.random().toString(16).slice(2);
  }

  function formatTime(epochSec) {
    try {
      var d = new Date(epochSec * 1000);
      var today = new Date();
      var sameDay = d.toDateString() === today.toDateString();
      return sameDay
        ? d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
        : d.toLocaleDateString([], { month: 'short', day: 'numeric' }) +
            ' · ' + d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    } catch (e) { return ''; }
  }

  // ── Session bootstrap ───────────────────────────────────────────────────
  // Each actor type exchanges whatever credential it has for a session token,
  // once. Cached in sessionStorage so a reload doesn't re-exchange.

  // Scoped to the account, because sessionStorage outlives a sign-out. On one
  // unscoped key, signing out and back in as someone else in the same tab
  // handed the new account the PREVIOUS account's session token — so the chat
  // loaded the previous account's thread and showed their messages, or an empty
  // pane where the current account's should have been.
  //
  // A link session has no signed-in customer to key on; it is already scoped by
  // the capability token it was minted from.
  function cacheKey(auth) {
    if (auth === 'link') return 'tn_link_sess';
    var who = tn().customerId || 'anon';
    return (auth === 'staff' ? 'tn_staff_sess_' : 'tn_chat_sess_') + who;
  }

  function readCachedSession(auth) {
    try {
      var raw = sessionStorage.getItem(cacheKey(auth));
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      if (!parsed.token || !parsed.expiresAt) return null;
      // 60s of slack so we don't hand back a token that dies mid-request.
      if (parsed.expiresAt * 1000 < Date.now() + 60000) return null;
      return parsed;
    } catch (e) { return null; }
  }

  function writeCachedSession(auth, token, expiresAt, extra) {
    try {
      sessionStorage.setItem(cacheKey(auth), JSON.stringify({
        token: token, expiresAt: expiresAt, threadId: (extra && extra.threadId) || null
      }));
    } catch (e) {}
  }

  function clearCachedSession(auth) {
    try { sessionStorage.removeItem(cacheKey(auth)); } catch (e) {}
  }

  function bootstrapSession(auth, force) {
    if (!force) {
      var cached = readCachedSession(auth);
      if (cached) return Promise.resolve(cached);
    }

    var t = tn();

    if (auth === 'link') {
      var params = new URLSearchParams(location.search);
      var linkToken = params.get('t');
      if (!linkToken) return Promise.reject(new Error('no-link-token'));

      return post('/api/chat/session', { linkToken: linkToken }).then(function (res) {
        if (!res.data.ok) throw new Error(res.data.error || 'session-failed');
        writeCachedSession(auth, res.data.sessionToken, res.data.expiresAt, {
          threadId: res.data.threadId
        });
        // Get the capability token out of the URL bar and out of any Referer.
        try {
          params.delete('t');
          var qs = params.toString();
          history.replaceState({}, '', location.pathname + (qs ? '?' + qs : ''));
        } catch (e) {}
        return {
          token: res.data.sessionToken,
          expiresAt: res.data.expiresAt,
          threadId: res.data.threadId,
          proofId: res.data.proofId
        };
      });
    }

    if (!t.customerId || !t.authSig) return Promise.reject(new Error('not-signed-in'));

    var path = auth === 'staff' ? '/api/staff/session' : '/api/chat/session';
    return post(path, { customerId: t.customerId, ts: t.authTs, sig: t.authSig }).then(function (res) {
      if (!res.data.ok) throw new Error(res.data.error || 'session-failed');
      writeCachedSession(auth, res.data.sessionToken, res.data.expiresAt, {});
      return { token: res.data.sessionToken, expiresAt: res.data.expiresAt };
    });
  }

  // ── Component ───────────────────────────────────────────────────────────

  function mount(root, opts) {
    if (!root) return null;
    if (root.dataset.tnChatMounted === '1') return root._tnChat || null;
    root.dataset.tnChatMounted = '1';

    opts = opts || {};
    var auth = opts.auth || 'customer';
    var role = opts.role || 'customer';
    var threadKey = opts.threadKey || { kind: 'profile' };

    var state = {
      session: null,
      threadId: threadKey.threadId || null,
      cursor: 0,
      lastActivity: Date.now(),
      backoff: 0,
      timer: null,
      destroyed: false,
      pending: {},          // clientId -> bubble node, for reconciliation
      renderedIds: {},
      readSent: 0,
      readTimer: null,
      proofs: {}            // proofId -> proof, so a proof_sent event can
                            // render as a full card instead of a text line
    };

    // ── DOM ──
    root.classList.add('tn-chat');
    var pane = el('div', 'tn-chat__pane');
    var list = el('div', 'tn-chat__list');
    // Screen readers announce messages as they arrive rather than the pane
    // updating silently. 'polite' waits for a pause instead of interrupting.
    list.setAttribute('role', 'log');
    list.setAttribute('aria-live', 'polite');
    list.setAttribute('aria-relevant', 'additions');
    pane.appendChild(list);

    var composer = el('form', 'tn-chat__composer');
    var attachBtn = el('button', 'tn-chat__attach');
    attachBtn.type = 'button';
    attachBtn.setAttribute('aria-label', 'Attach a photo');
    attachBtn.innerHTML =
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
      '<path d="M21.44 11.05l-8.49 8.49a5 5 0 0 1-7.07-7.07l8.49-8.49a3 3 0 0 1 4.24 4.24l-8.49 8.49a1 1 0 0 1-1.41-1.41l7.78-7.78"/></svg>';

    var fileInput = el('input');
    fileInput.type = 'file';
    fileInput.accept = 'image/*';
    fileInput.multiple = true;
    fileInput.hidden = true;

    var input = el('input', 'tn-chat__input');
    input.type = 'text';
    input.placeholder = opts.placeholder || 'Message Tali…';
    input.autocomplete = 'off';

    var sendBtn = el('button', 'tn-chat__send');
    sendBtn.type = 'submit';
    sendBtn.setAttribute('aria-label', 'Send');
    sendBtn.innerHTML =
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
      '<path d="M22 2L11 13"/><path d="M22 2l-7 20-4-9-9-4 20-7z"/></svg>';

    composer.appendChild(attachBtn);
    composer.appendChild(fileInput);
    composer.appendChild(input);
    composer.appendChild(sendBtn);

    var status = el('div', 'tn-chat__status');

    // Photos wait here until the message is actually sent.
    //
    // Picking a file used to call send() on the spot, so an attachment went the
    // instant it was chosen with no chance to look at it, add a note, or change
    // your mind. Nothing else on the site behaves that way.
    var tray = el('div', 'tn-chat__tray');
    tray.hidden = true;
    var staged = [];

    root.appendChild(pane);
    root.appendChild(tray);
    root.appendChild(composer);
    root.appendChild(status);

    function stageFiles(fileList) {
      var picked = Array.prototype.slice.call(fileList || []);
      var rejected = '';
      for (var i = 0; i < picked.length; i++) {
        if (staged.length >= MAX_FILES) { rejected = 'You can attach up to ' + MAX_FILES + ' photos.'; break; }
        if (picked[i].type.indexOf('image/') !== 0) { rejected = 'Only photos can be attached.'; continue; }
        if (picked[i].size > MAX_FILE_BYTES) { rejected = 'That photo is too large.'; continue; }
        staged.push(picked[i]);
      }
      setStatus(rejected, rejected ? 'error' : '');
      renderTray();
    }

    function clearStaged() {
      staged.length = 0;
      renderTray();
    }

    function renderTray() {
      // Revoke before wiping. Every thumbnail holds a blob URL, and without
      // this each pick leaks one for the life of the page.
      Array.prototype.forEach.call(tray.querySelectorAll('img'), function (img) {
        URL.revokeObjectURL(img.src);
      });
      tray.innerHTML = '';
      tray.hidden = !staged.length;
      if (!staged.length) return;

      staged.forEach(function (file, index) {
        var cell = el('div', 'tn-chat__thumb');
        var img = document.createElement('img');
        img.src = URL.createObjectURL(file);
        img.alt = '';
        var x = el('button', 'tn-chat__thumb-x', '\u00d7');
        x.type = 'button';
        x.setAttribute('aria-label', 'Remove this photo');
        x.addEventListener('click', function () {
          staged.splice(index, 1);
          renderTray();
        });
        cell.appendChild(img);
        cell.appendChild(x);
        tray.appendChild(cell);
      });
    }

    function setStatus(msg, kind) {
      status.textContent = msg || '';
      status.className = 'tn-chat__status' + (kind ? ' tn-chat__status--' + kind : '');
    }

    function stub(message, actionHtml) {
      root.innerHTML = '<div class="tn-chat__stub">' + message + (actionHtml || '') + '</div>';
    }

    function atBottom() {
      return pane.scrollHeight - pane.scrollTop - pane.clientHeight < 60;
    }

    function scrollToEnd() { pane.scrollTop = pane.scrollHeight; }

    // ── Rendering ──

    function money(cents) { return '$' + (Number(cents || 0) / 100).toFixed(2); }

    // A proof arrives in the thread as a system message carrying a proofId.
    // Rendering it as a full card is what makes the conversation the single
    // place a customer reviews, approves, pays and asks questions.
    function proofCard(proof) {
      var node = el('div', 'tn-chat__proof');

      var img = el('img', 'tn-chat__proof-img');
      img.src = proof.imageUrl;
      img.alt = proof.title;
      img.loading = 'lazy';
      img.addEventListener('click', function () { window.open(proof.imageUrl, '_blank', 'noopener'); });
      node.appendChild(img);

      var body = el('div', 'tn-chat__proof-body');
      body.appendChild(el('div', 'tn-chat__proof-eyebrow',
        proof.revision > 1 ? 'Your proof · version ' + proof.revision : 'Your proof'));
      body.appendChild(el('div', 'tn-chat__proof-title', proof.title));
      if (proof.notes) body.appendChild(el('div', 'tn-chat__proof-notes', proof.notes));

      var price = el('div', 'tn-chat__proof-price');
      price.innerHTML =
        '<div><span>Set total</span><span>' + money(proof.totalCents) + '</span></div>' +
        (proof.depositCents > 0
          ? '<div class="is-credit"><span>Deposit already paid</span><span>− ' + money(proof.depositCents) + '</span></div>'
          : '') +
        '<div class="is-total"><span>Balance due</span><span>' + money(proof.balanceCents) + '</span></div>';
      body.appendChild(price);

      // Only the customer side ever acts on a proof.
      if (role === 'customer') {
        if (proof.status === 'sent' || proof.status === 'changes_requested') {
          if (proof.status === 'changes_requested') {
            body.appendChild(el('div', 'tn-chat__proof-note',
              'You asked for changes — Tali is working on a new version.'));
          }
          var actions = el('div', 'tn-chat__proof-actions');

          var approve = el('button', 'tn-chat__proof-btn', 'Approve & pay ' + money(proof.balanceCents));
          approve.type = 'button';
          approve.addEventListener('click', function () { doApprove(proof, approve); });

          var changes = el('button', 'tn-chat__proof-btn tn-chat__proof-btn--ghost', 'Request changes');
          changes.type = 'button';
          changes.addEventListener('click', function () { askForChanges(proof); });

          actions.appendChild(approve);
          actions.appendChild(changes);
          body.appendChild(actions);
        } else if (proof.status === 'approved') {
          var pay = el('button', 'tn-chat__proof-btn', 'Pay balance — ' + money(proof.balanceCents) + ' →');
          pay.type = 'button';
          pay.addEventListener('click', function () { doCheckout(proof, pay); });
          var wrap = el('div', 'tn-chat__proof-actions');
          wrap.appendChild(pay);
          body.appendChild(wrap);
        } else if (proof.status === 'paid') {
          body.appendChild(el('div', 'tn-chat__proof-paid', 'Paid ✦ — your set is in production'));
        } else if (proof.status === 'superseded') {
          body.appendChild(el('div', 'tn-chat__proof-note', 'Replaced by a newer version below.'));
        }
      } else {
        body.appendChild(el('div', 'tn-chat__proof-note', 'Status: ' + proof.status.replace(/_/g, ' ')));
      }

      node.appendChild(body);
      return node;
    }

    function bubbleFor(msg) {
      var mine = msg.sender === role;
      var node = el('div', 'tn-chat__msg tn-chat__msg--' + (msg.sender === 'system' ? 'system' : (mine ? 'me' : 'them')));

      if (msg.sender === 'system') {
        // Proof events become cards; everything else stays a small pill.
        if (msg.proofId && msg.event === 'proof_sent' && state.proofs[msg.proofId]) {
          var card = el('div', 'tn-chat__msg tn-chat__msg--proof');
          card.appendChild(proofCard(state.proofs[msg.proofId]));
          return card;
        }
        // A profile edit is now a one-line status like any other system event,
        // so it renders as the ordinary pill. Older threads still hold the
        // multi-line "Label: old → new" bodies from when this was a card; the
        // pill shows them as-is rather than hiding history.
        node.appendChild(el('div', 'tn-chat__system', msg.body));
        return node;
      }

      var bubble = el('div', 'tn-chat__bubble');
      if (msg.body) bubble.appendChild(el('div', 'tn-chat__text', msg.body));

      (msg.attachments || []).forEach(function (att) {
        var a = el('a', 'tn-chat__photo');
        // Full size behind the click, resized copy in the bubble.
        a.href = att.url;
        a.target = '_blank';
        a.rel = 'noopener';
        var img = el('img');
        img.src = thumbUrl(att.url);
        img.alt = '';
        img.loading = 'lazy';
        a.appendChild(img);
        bubble.appendChild(a);
      });

      node.appendChild(bubble);
      node.appendChild(el('div', 'tn-chat__meta', formatTime(msg.createdAt)));
      return node;
    }

    function renderMessages(messages, opts2) {
      var wasAtBottom = atBottom();
      var appended = false;

      messages.forEach(function (msg) {
        if (state.renderedIds[msg.id]) return;

        // If this is the server's copy of a message we already drew
        // optimistically, upgrade that node instead of adding a second one.
        var pendingNode = msg.clientId && state.pending[msg.clientId];
        if (pendingNode) {
          var replacement = bubbleFor(msg);
          pendingNode.parentNode.replaceChild(replacement, pendingNode);
          delete state.pending[msg.clientId];
          state.renderedIds[msg.id] = true;
          appended = true;
          return;
        }

        list.appendChild(bubbleFor(msg));
        state.renderedIds[msg.id] = true;
        appended = true;
      });

      if (appended && (wasAtBottom || (opts2 && opts2.force))) scrollToEnd();
      updateEmptyState();
    }

    function updateEmptyState() {
      var has = list.children.length > 0;
      if (!has && !root.querySelector('.tn-chat__empty')) {
        var empty = el('div', 'tn-chat__empty');
        empty.innerHTML = opts.emptyState ||
          '<strong>Questions about your photos?</strong><span>Send Tali a message — she\'ll reply right here.</span>';
        list.appendChild(empty);
      } else if (has) {
        var e = list.querySelector('.tn-chat__empty');
        if (e && list.children.length > 1) e.remove();
      }
    }

    // ── Proof actions ──

    function reload() {
      // A proof status change rewrites an existing card, so redraw the whole
      // thread rather than trying to patch one node in place.
      return post('/api/chat/thread', { sessionToken: state.session.token, threadId: state.threadId })
        .then(function (res) {
          if (!res.data.ok) return;
          state.proofs = {};
          (res.data.proofs || []).forEach(function (p) { state.proofs[p.id] = p; });
          list.innerHTML = '';
          state.renderedIds = {};
          state.pending = {};
          renderMessages(res.data.messages, { force: true });
          state.cursor = res.data.cursor;
          updateEmptyState();
        });
    }

    function doApprove(proof, btn) {
      if (!confirm('Approve this design and continue to payment?')) return;
      btn.disabled = true;
      btn.textContent = 'Approving…';
      post('/api/proofs/respond', {
        sessionToken: state.session.token,
        proofId: proof.id,
        revision: proof.revision,
        action: 'approve'
      }).then(function (res) {
        if (res.data.error === 'superseded') {
          setStatus('Tali sent a newer version — loading it now.');
          return reload();
        }
        if (!res.data.ok) {
          btn.disabled = false;
          btn.textContent = 'Approve & pay ' + money(proof.balanceCents);
          setStatus(res.data.error || 'Something went wrong — message Tali.', 'error');
          return;
        }
        if (res.data.invoiceUrl) { window.location.href = res.data.invoiceUrl; return; }
        reload();
      }).catch(function () {
        btn.disabled = false;
        btn.textContent = 'Approve & pay ' + money(proof.balanceCents);
      });
    }

    function doCheckout(proof, btn) {
      btn.disabled = true;
      btn.textContent = 'Opening checkout…';
      post('/api/proofs/checkout', { sessionToken: state.session.token, proofId: proof.id })
        .then(function (res) {
          if (res.data.ok && res.data.invoiceUrl) { window.location.href = res.data.invoiceUrl; return; }
          btn.disabled = false;
          btn.textContent = 'Pay balance — ' + money(proof.balanceCents) + ' →';
          setStatus(res.data.error || 'Could not open checkout — message Tali.', 'error');
        })
        .catch(function () {
          btn.disabled = false;
          btn.textContent = 'Pay balance — ' + money(proof.balanceCents) + ' →';
        });
    }

    // Reuses the normal composer rather than a modal — the customer types
    // their change request exactly where they'd type any other message.
    function askForChanges(proof) {
      input.placeholder = 'What would you like changed?';
      input.focus();
      setStatus('Type your changes below, then hit send.');
      state.pendingChangeProof = proof;
    }

    function submitChangeRequest(proof, text) {
      state.pendingChangeProof = null;
      input.placeholder = opts.placeholder || 'Message Tali…';
      setStatus('Sending…');
      return post('/api/proofs/respond', {
        sessionToken: state.session.token,
        proofId: proof.id,
        revision: proof.revision,
        action: 'request_changes',
        comment: text
      }).then(function (res) {
        if (!res.data.ok) { setStatus(res.data.error || 'Could not send that.', 'error'); return; }
        setStatus('');
        return reload();
      });
    }

    // ── Read pointer ──
    // Debounced, and only when it actually advances. A mark-read-per-poll
    // would burn the D1 daily write budget for nothing.

    function maybeMarkRead() {
      if (state.cursor <= state.readSent) return;
      clearTimeout(state.readTimer);
      state.readTimer = setTimeout(function () {
        var upTo = state.cursor;
        if (upTo <= state.readSent) return;
        post('/api/chat/read', {
          sessionToken: state.session.token, threadId: state.threadId, upToId: upTo
        }).then(function () {
          state.readSent = upTo;
          // Only a customer's own thread drives the nav badge. Tali reading a
          // customer's thread inside headquarters must not clear her personal one.
          if (role === 'customer' && window.TN && window.TN.clearUnreadBadge) {
            window.TN.clearUnreadBadge();
          }
        }).catch(function () {});
      }, 1000);
    }

    // Opening the thread is a read, and until now nothing said so to the server.
    // A tab opened in the background (cmd-click, prerender) has NOT been read
    // yet, so wait for it to actually be looked at before claiming otherwise.
    //
    // CUSTOMER ONLY, deliberately. In headquarters the unread flag is what puts a
    // thread on Tali's "needs reply" board, and replying is what clears it. If
    // merely opening one cleared it, a thread she peeked at and got pulled away
    // from would silently stop asking for an answer.
    function markReadWhenVisible() {
      if (role !== 'customer') return;
      if (document.visibilityState === 'visible') { maybeMarkRead(); return; }
      var onVis = function () {
        if (document.visibilityState !== 'visible') return;
        document.removeEventListener('visibilitychange', onVis);
        maybeMarkRead();
      };
      document.addEventListener('visibilitychange', onVis);
    }

    // ── Polling ──

    function nextDelay() {
      if (state.backoff) return Math.min(state.backoff, MAX_BACKOFF_MS);
      var active = Date.now() - state.lastActivity < ACTIVE_WINDOW_MS;
      return active ? POLL_ACTIVE_MS : POLL_IDLE_MS;
    }

    function schedule() {
      clearTimeout(state.timer);
      if (state.destroyed) return;
      if (document.visibilityState === 'hidden') return; // resumed by the listener
      state.timer = setTimeout(poll, nextDelay());
    }

    function poll() {
      if (state.destroyed || !state.session || !state.threadId) return;

      post('/api/chat/poll', {
        sessionToken: state.session.token,
        threadId: state.threadId,
        sinceId: state.cursor
      }).then(function (res) {
        if (res.status === 401) return handleAuthExpired();
        if (!res.data.ok) { state.backoff = state.backoff ? state.backoff * 2 : 5000; return schedule(); }

        state.backoff = 0;
        if (res.data.messages && res.data.messages.length) {
          state.lastActivity = Date.now();
          // A proof event polled in has no proof record yet — refetch the
          // thread so the card renders instead of an empty system pill.
          if (res.data.messages.some(function (m) { return m.proofId && m.event; })) {
            reload();
            state.cursor = res.data.cursor;
            schedule();
            return;
          }
          renderMessages(res.data.messages);
          state.cursor = res.data.cursor;
          if (document.visibilityState === 'visible') maybeMarkRead();
          if (opts.onUnread) opts.onUnread(res.data.thread && res.data.thread.unread ? 1 : 0);
        }
        schedule();
      }).catch(function () {
        state.backoff = state.backoff ? state.backoff * 2 : 5000;
        schedule();
      });
    }

    var authRetried = false;
    function handleAuthExpired() {
      clearTimeout(state.timer);
      if (!authRetried) {
        authRetried = true;
        clearCachedSession(auth);
        return bootstrapSession(auth, true).then(function (sess) {
          state.session = sess;
          authRetried = false;
          poll();
        }).catch(function () {
          setStatus('Session expired — refresh the page to keep chatting.', 'error');
          if (opts.onAuthExpired) opts.onAuthExpired();
        });
      }
      setStatus('Session expired — refresh the page to keep chatting.', 'error');
      if (opts.onAuthExpired) opts.onAuthExpired();
    }

    document.addEventListener('visibilitychange', function () {
      if (state.destroyed) return;
      if (document.visibilityState === 'visible') { state.backoff = 0; poll(); }
      else clearTimeout(state.timer);
    });

    // ── Sending ──

    function uploadFiles(files) {
      var uploader = window.CloudinaryUpload;
      if (!uploader) return Promise.resolve([]);
      var rawName = opts.customerName || 'unknown';
      var safeName = String(rawName).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'unknown';
      var folder = 'talisnailz/' + safeName + '/chat/' + (state.threadId || 'unknown');
      var picked = Array.prototype.slice.call(files, 0, MAX_FILES).filter(function (f) {
        return f.type.indexOf('image/') === 0 && f.size <= MAX_FILE_BYTES;
      });
      if (!picked.length) return Promise.resolve([]);

      // Real progress, not a static word. A 4MB photo can take minutes on a
      // phone connection, and with no number moving the whole app reads as
      // frozen -- which is exactly how this was first reported.
      var pcts = picked.map(function () { return 0; });
      function reportProgress() {
        var avg = Math.round(pcts.reduce(function (a, b) { return a + b; }, 0) / pcts.length);
        setStatus('Sending ' + (picked.length > 1 ? picked.length + ' photos… ' : 'photo… ') + avg + '%');
      }
      reportProgress();

      return Promise.all(picked.map(function (f, i) {
        return uploader.upload(f, function (pct) { pcts[i] = pct; reportProgress(); }, folder).then(function (r) {
          return { url: r.url, publicId: r.publicId, width: r.width, height: r.height };
        });
      })).then(function (list2) { setStatus(''); return list2; })
        .catch(function () { setStatus("Couldn't upload that photo.", 'error'); return []; });
    }

    function send(text, files) {
      if (!state.session || !state.threadId) return Promise.resolve(false);
      text = (text || '').trim();

      return uploadFiles(files || []).then(function (attachments) {
        if (!text && !attachments.length) return false;

        // Committed from here on: a bubble goes up, and if the post fails the
        // retry reuses these already-uploaded attachments. So this is the only
        // safe moment to empty the tray -- earlier and a failed upload throws
        // the customer's photos away, later and a retry would send them twice.
        clearStaged();

        var clientId = uuid();
        var optimistic = bubbleFor({
          sender: role, body: text, attachments: attachments, createdAt: Math.floor(Date.now() / 1000)
        });
        optimistic.classList.add('tn-chat__msg--pending');
        var emptyNode = list.querySelector('.tn-chat__empty');
        if (emptyNode) emptyNode.remove();
        list.appendChild(optimistic);
        state.pending[clientId] = optimistic;
        scrollToEnd();
        state.lastActivity = Date.now();

        return post('/api/chat/send', {
          sessionToken: state.session.token,
          threadId: state.threadId,
          body: text,
          attachments: attachments,
          clientId: clientId
        }).then(function (res) {
          if (res.status === 401) { handleAuthExpired(); throw new Error('auth'); }
          if (!res.data.ok) throw new Error(res.data.error || 'send-failed');

          var msg = res.data.message;
          var node = state.pending[clientId];
          if (node) {
            node.parentNode.replaceChild(bubbleFor(msg), node);
            delete state.pending[clientId];
          }
          state.renderedIds[msg.id] = true;
          if (msg.id > state.cursor) state.cursor = msg.id;
          state.readSent = state.cursor;
          scrollToEnd();
          state.backoff = 0;
          schedule();
          return true;
        }).catch(function () {
          var node = state.pending[clientId];
          if (node) {
            node.classList.remove('tn-chat__msg--pending');
            node.classList.add('tn-chat__msg--failed');
            node.title = 'Tap to retry';
            // Retry reuses the SAME clientId, so the server dedupes it.
            node.onclick = function () {
              node.onclick = null;
              node.classList.remove('tn-chat__msg--failed');
              node.classList.add('tn-chat__msg--pending');
              post('/api/chat/send', {
                sessionToken: state.session.token, threadId: state.threadId,
                body: text, attachments: attachments, clientId: clientId
              }).then(function (r2) {
                if (r2.data && r2.data.ok) {
                  node.parentNode.replaceChild(bubbleFor(r2.data.message), node);
                  delete state.pending[clientId];
                  state.renderedIds[r2.data.message.id] = true;
                } else {
                  node.classList.remove('tn-chat__msg--pending');
                  node.classList.add('tn-chat__msg--failed');
                }
              }).catch(function () {
                node.classList.remove('tn-chat__msg--pending');
                node.classList.add('tn-chat__msg--failed');
              });
            };
          }
          return false;
        });
      });
    }

    composer.addEventListener('submit', function (e) {
      e.preventDefault();
      var text = input.value;
      // Photos on their own are a message. The old guard was `if (!text.trim())
      // return`, which is exactly why picking a file had to send immediately:
      // it was the only way an attachment could ever leave the composer.
      if (!text.trim() && !staged.length) return;

      if (state.pendingChangeProof) {
        // A change request is text only. Anything staged is left in the tray
        // rather than silently dropped, so it can be sent as its own message.
        if (!text.trim()) return;
        input.value = '';
        submitChangeRequest(state.pendingChangeProof, text.trim());
        return;
      }

      input.value = '';
      // Locked until this one finishes. Without it a slow photo upload looks
      // like a dead button, so you press send again, and again, and end up with
      // several messages queued behind each other.
      sendBtn.disabled = true;
      var release = function () { sendBtn.disabled = false; };
      // The tray is cleared inside send(), at the point the message is actually
      // committed. Clearing it here would discard the photos when an upload
      // fails, which is the one moment the customer most needs them back.
      send(text, staged.slice()).then(release, release);
    });

    attachBtn.addEventListener('click', function () { fileInput.click(); });
    fileInput.addEventListener('change', function () {
      stageFiles(fileInput.files);
      // Reset so choosing the SAME file again still fires a change event.
      fileInput.value = '';
    });

    // ── Boot ──

    if (!workerUrl()) {
      stub('<strong>Messaging isn\'t set up yet.</strong><span>Check back soon.</span>');
      return null;
    }

    setStatus('Loading…');

    bootstrapSession(auth).then(function (sess) {
      state.session = sess;
      if (sess.threadId) state.threadId = sess.threadId;

      var body = { sessionToken: sess.token };
      if (state.threadId) body.threadId = state.threadId;
      else if (threadKey.kind) body.threadKind = threadKey.kind;

      return post('/api/chat/thread', body).then(function (res) {
        if (res.status === 401) return handleAuthExpired();
        if (!res.data.ok) throw new Error(res.data.error || 'thread-failed');

        state.threadId = res.data.thread.id;
        setStatus('');
        (res.data.proofs || []).forEach(function (p) { state.proofs[p.id] = p; });
        renderMessages(res.data.messages, { force: true });
        state.cursor = res.data.cursor;
        // This used to be `state.readSent = res.data.cursor`, which marked the
        // thread read LOCALLY and never posted /api/chat/read. maybeMarkRead()
        // then short-circuited forever, customer_last_read_id never advanced,
        // and the nav badge came back on every page load for good. Opening the
        // thread has to tell the server, not just the client.
        state.readSent = 0;
        markReadWhenVisible();
        updateEmptyState();
        schedule();
      });
    }).catch(function (err) {
      var reason = err && err.message;
      if (reason === 'not-signed-in') {
        stub(
          '<strong>Sign in to message Tali</strong><span>Your conversation stays with your account.</span>',
          '<a class="btn btn--primary" href="/customer_authentication/login?return_to=' +
            encodeURIComponent('/pages/nail-profile') + '">Sign in</a>'
        );
      } else if (reason === 'no-link-token') {
        stub('<strong>This link is missing its access code.</strong><span>Open the link from your email again.</span>');
      } else {
        stub('<strong>Couldn\'t load your messages.</strong><span>Refresh the page to try again.</span>');
      }
    });

    var api = {
      send: function (text, files) { return send(text, files); },
      refresh: function () { state.backoff = 0; poll(); },
      destroy: function () {
        state.destroyed = true;
        clearTimeout(state.timer);
        clearTimeout(state.readTimer);
        root.dataset.tnChatMounted = '';
      }
    };
    root._tnChat = api;
    return api;
  }

  window.TNChat = { mount: mount };
})();
